// ============================================================================
// TicketingClient — thin Connect/gRPC-over-JSON transport over `fetch`.
//
// The go-ticketing service exposes Connect endpoints at:
//   <baseUrl>/<package>.<Service>/<Method>      (POST, application/json)
//
// baseUrl is blank in dev/prod (same-origin) and the request is proxied to the
// backend (the service has no CORS, so same-origin is required). Every RPC is
// tenant-scoped — `tenant_id` is injected from runtime config.
//
// This is the transport primitive only; typed per-service wrappers (TicketService,
// StaffService, SetupService, AttachmentService, AIAssistService) build on top.
// ============================================================================
import { appConfigService } from '../app-config';
import { getAccessToken } from '../oidc';

/** Session-expiry hook, registered by authService (kept as a callback so the
 *  transport does not import the auth module: auth → staff api → transport). */
let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(fn: () => void): void {
  unauthorizedHandler = fn;
}

/** Bearer header for direct-Keycloak (oidc) mode; empty for mock/bff (cookie auth). */
function authHeader(): Record<string, string> {
  const t = getAccessToken();
  return t ? { authorization: `Bearer ${t}` } : {};
}

export class ConnectError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly httpStatus: number,
  ) {
    super(message);
    this.name = 'ConnectError';
  }
}

export class TicketingClient {
  private readonly appConfig = appConfigService;

  /** A 401 on a live (cookie/BFF) call means the session died — bounce to login
   *  once (debounced in AuthService). Silent calls (AI assist) opt out so an
   *  unavailable AI module never forces a logout. */
  private onUnauthorized(): void {
    // Mock sign-in has no session that could expire: against a local open-mode
    // backend, identity-only services (notifications, presence) answer 401 and
    // must not bounce the dev user to /login.
    if (this.enabled && this.appConfig.snapshot.authMode !== 'mock') unauthorizedHandler?.();
  }

  get enabled(): boolean {
    return this.appConfig.snapshot.ticketing.enabled;
  }

  get tenantId(): string {
    return this.appConfig.snapshot.ticketing.tenantId;
  }

  /**
   * Invoke a unary Connect RPC.
   * @param service fully-qualified service, e.g. 'ticketing.v1.TicketService'
   * @param method  RPC method name, e.g. 'ListTickets'
   * @param message request body (tenant_id is merged in automatically)
   * @param opts.silent401 when true, a 401 won't bubble to the auth interceptor concept
   */
  async unary<TRes>(
    service: string,
    method: string,
    message: Record<string, unknown> = {},
    opts: { signal?: AbortSignal; headers?: Record<string, string>; silent401?: boolean } = {},
  ): Promise<TRes> {
    const base = this.appConfig.snapshot.ticketing.baseUrl;
    const url = `${base}/${service}/${method}`;
    const body = this.tenantId ? { tenant_id: this.tenantId, ...message } : message;

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      // x-ld-bff marks the request for the BFF's CSRF gate (cookie-authed mode); a
      // cross-site form can't set it, and it's ignored by the backend in direct mode.
      headers: {
        'content-type': 'application/json',
        'x-ld-bff': '1',
        ...authHeader(),
        ...(opts.headers ?? {}),
      },
      body: JSON.stringify(body),
      signal: opts.signal,
    });

    if (!res.ok) {
      let code = 'unknown';
      let detail = res.statusText;
      try {
        const err = (await res.json()) as { code?: string; message?: string };
        code = err.code ?? code;
        detail = err.message ?? detail;
      } catch {
        /* non-JSON error body */
      }
      if (res.status === 401 && !opts.silent401) this.onUnauthorized();
      throw new ConnectError(code, detail, res.status);
    }

    return (await res.json()) as TRes;
  }

  /**
   * Invoke a server-streaming Connect RPC, yielding each response message as it
   * arrives. Uses the Connect *streaming* protocol (content-type
   * `application/connect+json`): every message — the single request and each
   * response — is wrapped in a 5-byte prefix `[flags:1][length:4 BE]` ahead of
   * its JSON payload. The final response frame sets the end-stream bit (0b10);
   * its payload is `{}` on success or `{error:{code,message}}` on failure.
   *
   * This mirrors the framing the BFF uses to bridge SubscribeTickets→SSE, ported
   * to the browser with `fetch` + `ReadableStream` so AI streams stay SPA-direct
   * (see the ai-assist-transport note). Abort mid-stream via `opts.signal`.
   */
  async *serverStream<TRes>(
    service: string,
    method: string,
    message: Record<string, unknown> = {},
    opts: { signal?: AbortSignal; headers?: Record<string, string>; silent401?: boolean } = {},
  ): AsyncGenerator<TRes, void, unknown> {
    const base = this.appConfig.snapshot.ticketing.baseUrl;
    const url = `${base}/${service}/${method}`;
    const body = this.tenantId ? { tenant_id: this.tenantId, ...message } : message;

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/connect+json',
        'connect-protocol-version': '1',
        ...authHeader(),
        ...(opts.headers ?? {}),
      },
      body: encodeEnvelope(0, body),
      signal: opts.signal,
    });

    if (!res.ok || !res.body) {
      // The stream can fail before any frame (401/404/unimplemented/precondition).
      let code = 'unknown';
      let detail = res.statusText;
      try {
        const err = (await res.json()) as { code?: string; message?: string };
        code = err.code ?? code;
        detail = err.message ?? detail;
      } catch {
        /* non-JSON error body */
      }
      if (res.status === 401 && !opts.silent401) this.onUnauthorized();
      throw new ConnectError(code, detail, res.status);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = new Uint8Array(0);
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) buf = concat(buf, value);
      // Drain every complete envelope currently buffered.
      for (;;) {
        if (buf.length < 5) break;
        const flags = buf[0];
        const len = ((buf[1] << 24) | (buf[2] << 16) | (buf[3] << 8) | buf[4]) >>> 0;
        if (buf.length < 5 + len) break;
        const payload = buf.subarray(5, 5 + len);
        buf = buf.subarray(5 + len);
        const json = payload.length ? JSON.parse(decoder.decode(payload)) : {};
        if (flags & 0x02) {
          // End-of-stream frame: surface a trailing error if present, else stop.
          const e = (json as { error?: { code?: string; message?: string } }).error;
          if (e) throw new ConnectError(e.code ?? 'unknown', e.message ?? 'stream error', res.status);
          return;
        }
        yield json as TRes;
      }
    }
  }
}

/** Wrap a message as a Connect stream envelope: `[flags][len:4 BE][utf8 json]`. */
function encodeEnvelope(flags: number, message: unknown): Uint8Array<ArrayBuffer> {
  const json = new TextEncoder().encode(JSON.stringify(message));
  const frame = new Uint8Array(5 + json.length);
  frame[0] = flags;
  frame[1] = (json.length >>> 24) & 0xff;
  frame[2] = (json.length >>> 16) & 0xff;
  frame[3] = (json.length >>> 8) & 0xff;
  frame[4] = json.length & 0xff;
  frame.set(json, 5);
  return frame;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

export const ticketingClient = new TicketingClient();
